import React, { useState, useRef, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import { Mic, Upload, X, FileText, Video, Music, Image as ImageIcon, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { voiceRecognitionService } from '../../../services/voiceRecognitionService';

const EnhancedAdditionalContext = ({ formData, onChange, errors }) => {
  const [mode, setMode] = useState('freeflow'); // 'freeflow' or 'guided'
  const [isRecording, setIsRecording] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [recordingError, setRecordingError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [retryingConnection, setRetryingConnection] = useState(false);
  
  const fileInputRef = useRef(null);
  const formDataRef = useRef(formData);
  const onChangeRef = useRef(onChange);

  // Update refs when props change
  useEffect(() => {
    formDataRef.current = formData;
    onChangeRef.current = onChange;
  }, [formData, onChange]);

  const reflectivePrompts = [
    "If you were telling someone the story of your life so far, what would you say this current chapter is about?",
    "What tends to stay with you at the end of a long day, when everything goes quiet?",
    "What situations or moments seem to take the most out of you—and what helps you recover?",
    "When you feel most like yourself, what's usually happening around you?",
    "Is there something about you that people often miss or misunderstand?"
  ];

  // Initialize voice recognition service and register callbacks
  useEffect(() => {
    if (!voiceRecognitionService?.isSupported()) {
      return;
    }

    // Register callbacks for voice recognition events
    voiceRecognitionService?.setCallbacks({
      onTranscript: (transcript, fieldId) => {
        console.log('[Component] Received transcript:', transcript, 'for field:', fieldId);
        setRecordingError(null);
        setRetryingConnection(false);
        
        const currentFormData = formDataRef?.current;
        const currentOnChange = onChangeRef?.current;
        
        if (fieldId === 'freeflow') {
          const currentText = currentFormData?.additionalContext || '';
          currentOnChange('additionalContext', currentText + transcript);
        } else if (fieldId?.startsWith('prompt')) {
          const responses = currentFormData?.reflectivePromptsResponses || {};
          const currentText = responses?.[fieldId] || '';
          currentOnChange('reflectivePromptsResponses', {
            ...responses,
            [fieldId]: currentText + transcript
          });
        }
      },
      onError: (errorMessage) => {
        console.error('[Component] Recognition error:', errorMessage);
        // Check if it's a network error that will be retried
        if (errorMessage?.includes('Network error') && !errorMessage?.includes('persists')) {
          setRetryingConnection(true);
          setRecordingError('Reconnecting to speech service...');
        } else {
          setRecordingError(errorMessage);
          setIsRecording(false);
          setActiveField(null);
          setRetryingConnection(false);
        }
      },
      onStart: () => {
        console.log('[Component] Recognition started');
        setIsRecording(true);
        setRecordingError(null);
        setRetryingConnection(false);
      },
      onEnd: () => {
        console.log('[Component] Recognition ended');
        if (!retryingConnection) {
          setIsRecording(false);
          setActiveField(null);
        }
      }
    });

    // Cleanup on unmount
    return () => {
      voiceRecognitionService?.stopRecording();
    };
  }, []);

  const startVoiceDictation = async (fieldId) => {
    console.log('[Component] Start voice dictation for field:', fieldId);
    setRecordingError(null);
    setIsInitializing(true);

    try {
      // Set active field in component state
      setActiveField(fieldId);
      
      // Start recording via service (ensures initialization and proper state)
      await voiceRecognitionService?.startRecording(fieldId);
      
      console.log('[Component] Voice dictation started successfully');
    } catch (error) {
      console.error('[Component] Failed to start voice dictation:', error);
      setRecordingError(error?.message || 'Failed to start recording. Please try again.');
      setIsRecording(false);
      setActiveField(null);
    } finally {
      setIsInitializing(false);
    }
  };

  const stopVoiceDictation = async () => {
    console.log('[Component] Stop voice dictation');
    
    try {
      await voiceRecognitionService?.stopRecording();
      setIsRecording(false);
      setActiveField(null);
      setRecordingError(null);
    } catch (error) {
      console.error('[Component] Error stopping voice dictation:', error);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event?.target?.files);
    if (!files?.length) return;

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      alert('Please sign in to upload files');
      return;
    }

    for (const file of files) {
      // Validate file size
      const maxSize = file?.type?.startsWith('video/') ? 100 * 1024 * 1024 :
      file?.type?.startsWith('audio/') ? 50 * 1024 * 1024 :
      10 * 1024 * 1024;

      if (file?.size > maxSize) {
        alert(`File ${file?.name} exceeds size limit`);
        continue;
      }

      await uploadFile(file, user?.id);
    }
  };

  const uploadFile = async (file, userId) => {
    const fileId = Date.now() + '-' + file?.name;
    setUploadingFiles((prev) => [...prev, { id: fileId, name: file?.name, type: file?.type }]);
    setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));

    try {
      const timestamp = Date.now();
      const filePath = `${userId}/${timestamp}-${file?.name}`;

      const { data, error } = await supabase?.storage?.from('seeker-intake-media')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

      if (error) throw error;

      // Get signed URL for private bucket
      const { data: urlData, error: urlError } = await supabase?.storage?.from('seeker-intake-media')?.createSignedUrl(filePath, 31536000); // 1 year expiry

      if (urlError) throw urlError;

      // Add to context media URLs
      const currentMedia = formData?.contextMediaUrls || [];
      const newMedia = {
        id: fileId,
        name: file?.name,
        type: file?.type,
        size: file?.size,
        url: urlData?.signedUrl,
        path: filePath,
        uploadedAt: new Date()?.toISOString()
      };

      onChange('contextMediaUrls', [...currentMedia, newMedia]);

      setUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));
      setTimeout(() => {
        setUploadingFiles((prev) => prev?.filter((f) => f?.id !== fileId));
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress?.[fileId];
          return newProgress;
        });
      }, 1000);
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload ${file?.name}: ${error?.message}`);
      setUploadingFiles((prev) => prev?.filter((f) => f?.id !== fileId));
    }
  };

  const removeFile = async (fileId, filePath) => {
    try {
      // Delete from storage
      const { error } = await supabase?.storage?.from('seeker-intake-media')?.remove([filePath]);

      if (error) throw error;

      // Remove from state
      const currentMedia = formData?.contextMediaUrls || [];
      onChange('contextMediaUrls', currentMedia?.filter((m) => m?.id !== fileId));
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete file');
    }
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('video/')) return <Video className="w-4 h-4" />;
    if (type?.startsWith('audio/')) return <Music className="w-4 h-4" />;
    if (type?.startsWith('image/')) return <ImageIcon className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  const VoiceDictationButton = ({ fieldId }) => {
    const isActiveField = activeField === fieldId;
    const isThisFieldRecording = isRecording && isActiveField;
    const hasErrorForThisField = recordingError && isActiveField;
    const isOtherFieldRecording = isRecording && !isActiveField;

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 mt-2">
          {!isThisFieldRecording ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => startVoiceDictation(fieldId)}
              className="flex items-center gap-2"
              disabled={!voiceRecognitionService?.isSupported() || isOtherFieldRecording || isInitializing}
            >
              <Mic className="w-3 h-3" />
              {isInitializing && isActiveField ? 'Initializing...' : 'Start Voice Dictation'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={stopVoiceDictation}
            >
              Stop Recording
            </Button>
          )}
          {isThisFieldRecording && (
            <div className="flex items-center gap-2 text-xs text-destructive">
              <span className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
              Recording... (speak now)
            </div>
          )}
        </div>
        {hasErrorForThisField && (
          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {recordingError}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Additional Context (optional)
        </label>
        <p className="text-sm text-muted-foreground mb-3">
          Share more about your situation in your own words, or use our guided prompts
        </p>
      </div>
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-4">
        <Button
          type="button"
          variant={mode === 'freeflow' ? 'default' : 'outline'}
          onClick={() => {
            setMode('freeflow');
            // Stop any active recording when switching modes
            if (isRecording) {
              stopVoiceDictation();
            }
          }}
          className="flex-1"
        >
          Free Flow
        </Button>
        <Button
          type="button"
          variant={mode === 'guided' ? 'default' : 'outline'}
          onClick={() => {
            setMode('guided');
            // Stop any active recording when switching modes
            if (isRecording) {
              stopVoiceDictation();
            }
          }}
          className="flex-1"
        >
          Guided Questions
        </Button>
      </div>
      {/* Free Flow Mode */}
      {mode === 'freeflow' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <textarea
              className="w-full min-h-[200px] p-3 border border-border rounded-lg bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={`Choose to provide input in a free flow format thinking about answering questions like:

1. If you were telling someone the story of your life so far, what would you say this current chapter is about?

2. What tends to stay with you at the end of a long day, when everything goes quiet?

3. What situations or moments seem to take the most out of you—and what helps you recover?

4. When you feel most like yourself, what's usually happening around you?

5. Is there something about you that people often miss or misunderstand?`}
              value={formData?.additionalContext || ''}
              onChange={(e) => onChange('additionalContext', e?.target?.value)}
            />
            <VoiceDictationButton fieldId="freeflow" />
          </div>
          {!voiceRecognitionService?.isSupported() && (
            <p className="text-xs text-muted-foreground">
              Voice dictation is not supported in your browser. Please use Chrome or Safari.
            </p>
          )}
        </div>
      )}
      {/* Guided Questions Mode */}
      {mode === 'guided' && (
        <div className="space-y-4">
          {reflectivePrompts?.map((prompt, index) => (
            <div key={index} className="space-y-2 p-4 border border-border rounded-lg bg-card">
              <label className="text-sm font-medium text-foreground">
                {index + 1}. {prompt}
              </label>
              <textarea
                className="w-full min-h-[100px] p-3 border border-border rounded-lg bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Your response..."
                value={formData?.reflectivePromptsResponses?.[`prompt${index + 1}`] || ''}
                onChange={(e) => {
                  const responses = formData?.reflectivePromptsResponses || {};
                  onChange('reflectivePromptsResponses', {
                    ...responses,
                    [`prompt${index + 1}`]: e?.target?.value
                  });
                }}
              />
              <VoiceDictationButton fieldId={`prompt${index + 1}`} />
            </div>
          ))}
          {!voiceRecognitionService?.isSupported() && (
            <p className="text-xs text-muted-foreground">
              Voice dictation is not supported in your browser. Please use Chrome or Safari.
            </p>
          )}
        </div>
      )}
      {/* File Upload */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">
            Upload Medical reports, prescriptions, other information (optional)
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef?.current?.click()}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Choose Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Upload photos, videos, or audio files. Max sizes: Videos 100MB, Audio 50MB, Images 10MB
        </p>

        {/* Uploaded Files */}
        {formData?.contextMediaUrls?.length > 0 && (
          <div className="space-y-2">
            {formData?.contextMediaUrls?.map((file) => (
              <div
                key={file?.id}
                className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg"
              >
                <div className="text-muted-foreground">
                  {getFileIcon(file?.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {file?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file?.size)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeFile(file?.id, file?.path)}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Uploading Files */}
        {uploadingFiles?.length > 0 && (
          <div className="space-y-2">
            {uploadingFiles?.map((file) => (
              <div
                key={file?.id}
                className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg"
              >
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {file?.name}
                  </p>
                  <div className="w-full bg-border rounded-full h-1.5 mt-1">
                    <div
                      className="bg-primary h-1.5 rounded-full transition-all"
                      style={{ width: `${uploadProgress?.[file?.id] || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        This information helps us match you with the most appropriate helper and is kept confidential.
      </p>
    </div>
  );
};

export default EnhancedAdditionalContext;